import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { slugify } from "../../utils/slugify";

interface TocItem {
  slug: string;
  text: string;
  level: number;
}

function extractHeadings(markdown: string): TocItem[] {
  return [...markdown.matchAll(/^(#{1,3})\s+(.+)$/gm)]
    .filter((m) => m[1] && m[2])
    .map((m) => ({
      slug: slugify(m[2]!),
      text: m[2]!.replace(/[*_`[\]]/g, ""),
      level: m[1]!.length,
    }));
}

interface Props {
  markdown: string;
}

export const TableOfContents: React.FC<Props> = ({ markdown }) => {
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState("");
  const [open, setOpen] = useState(true);
  const headings = extractHeadings(markdown);

  useEffect(() => {
    if (!headings.length) return;
    const els = headings
      .map((h) => document.getElementById(h.slug))
      .filter(Boolean) as HTMLElement[];

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length && visible[0]) {
          setActiveSlug(visible[0].target.id);
        }
      },
      { rootMargin: "-5% 0px -80% 0px" }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [markdown]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!headings.length) return null;

  return (
    <nav className="toc" aria-label={t("toc.title")}>
      <button className="toc__header" onClick={() => setOpen((o) => !o)}>
        <span>{t("toc.title")}</span>
        <span className="toc__chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <ul className="toc__list">
          {headings.map((h) => (
            <li key={h.slug} className={`toc__item toc__item--h${h.level}`}>
              <a
                href={`#${h.slug}`}
                className={`toc__link${activeSlug === h.slug ? " toc__link--active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(h.slug);
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                  setActiveSlug(h.slug);
                }}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
};
